import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    Container,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AutocompleteArrayInput,
    BooleanInput,
    Form,
    NumberInput,
    ReferenceArrayInput,
    TextInput,
    Title,
    useDataProvider,
    useGetIdentity,
    useNotify,
} from 'react-admin';
import { Navigate } from 'react-router-dom';
import { isAdministrator } from '@/components/filters/CreatedByUserFilter';
import type { CrmDataProvider } from '@/providers/types';
import type { User } from '@/types';

export type SiteConfigOptionType = 'string' | 'text' | 'number' | 'boolean' | 'users';

export type SiteConfigOption = {
    key: string;
    label: string;
    type: SiteConfigOptionType;
    default: unknown;
    value: unknown;
    help_text?: string | null;
};

const OptionInput = ({ option }: { option: SiteConfigOption }) => {
    const helperText = option.help_text ?? undefined;
    switch (option.type) {
        case 'users':
            return (
                <ReferenceArrayInput
                    source={option.key}
                    reference="users"
                    perPage={25}
                    sort={{ field: 'name', order: 'ASC' }}
                >
                    <AutocompleteArrayInput
                        label={option.label}
                        helperText={helperText}
                        optionText={(record: User) => record?.name || record?.email}
                        fullWidth
                    />
                </ReferenceArrayInput>
            );
        case 'boolean':
            return (
                <BooleanInput
                    source={option.key}
                    label={option.label}
                    helperText={helperText}
                />
            );
        case 'number':
            return (
                <NumberInput
                    source={option.key}
                    label={option.label}
                    helperText={helperText}
                    fullWidth
                />
            );
        case 'text':
            return (
                <TextInput
                    source={option.key}
                    label={option.label}
                    helperText={helperText}
                    multiline
                    fullWidth
                />
            );
        case 'string':
        default:
            return (
                <TextInput
                    source={option.key}
                    label={option.label}
                    helperText={helperText}
                    fullWidth
                />
            );
    }
};

export const SiteConfigPage = () => {
    const { identity, isLoading: identityLoading } = useGetIdentity();
    const notify = useNotify();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const [options, setOptions] = useState<SiteConfigOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const isAdmin = isAdministrator(identity);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setOptions(await dataProvider.getSiteConfig());
        } catch {
            notify('Failed to load site config', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [notify, dataProvider]);

    useEffect(() => {
        if (identityLoading || !isAdmin) return;
        load();
    }, [identityLoading, isAdmin, load]);

    const record = useMemo(() => {
        const values: Record<string, unknown> = {};
        for (const option of options) {
            values[option.key] = option.value ?? option.default ?? null;
        }
        return values;
    }, [options]);

    if (identityLoading) return null;
    if (!isAdmin) return <Navigate to="/" replace />;

    const handleSubmit = async (values: Record<string, unknown>) => {
        setSaving(true);
        try {
            setOptions(await dataProvider.saveSiteConfig(values));
            notify('Site config saved');
        } catch {
            notify('Failed to save site config', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Container maxWidth="sm" sx={{ mt: 4 }}>
            <Title title="Site config" />
            <Typography variant="h5" gutterBottom>
                Site config
            </Typography>
            {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={28} />
                </Box>
            ) : options.length === 0 ? (
                <Typography color="text.secondary">
                    No configuration options defined yet.
                </Typography>
            ) : (
                <Form record={record} onSubmit={handleSubmit}>
                    <Card>
                        <CardContent>
                            {options.map(option => (
                                <OptionInput key={option.key} option={option} />
                            ))}
                        </CardContent>
                        <CardActions>
                            <Button type="submit" variant="contained" disabled={saving}>
                                Save
                            </Button>
                        </CardActions>
                    </Card>
                </Form>
            )}
        </Container>
    );
};

SiteConfigPage.path = '/site-config';
